import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../supabase/client';
import { upsertOwnedShare, upsertReceivedShare } from '../Redux/store';

/**
 * Hook for handling calendar share acceptance logic
 */
export function useCalendarShare() {
  const currentUserId = useSelector((s) => s.user.authUserId);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Accept a calendar share request
   * @param {uuid} ownerId - The user who owns the calendar being shared
   * @param {uuid} recipientId - The user who will receive the share
   */
  const acceptShare = async (ownerId, recipientId) => {
    setLoading(true);
    setError(null);

    try {
      // Check if share already exists to avoid duplicate key error
      const alreadyShared = await checkShared(ownerId, recipientId);
      if (alreadyShared) {
        return { success: true };
      }

      // Fetch profile data for both owner and recipient
      const { data: ownerProfile, error: ownerError } = await supabase
        .from('profiles')
        .select('id, name, role')
        .eq('id', ownerId)
        .single();

      const { data: recipientProfile, error: recipientError } = await supabase
        .from('profiles')
        .select('id, name, role')
        .eq('id', recipientId)
        .single();

      // Insert into calendar_shares table and return the inserted data
      const { data: insertedData, error: insertError } = await supabase
        .from('calendar_shares')
        .insert({
          owner_id: ownerId,
          shared_with_id: recipientId,
        })
        .select()
        .single();

      if (insertError) {
        console.error('[useCalendarShare] Insert error:', insertError);
        throw insertError;
      }

      // Update Redux state immediately
      if (insertedData) {
        if (ownerId === currentUserId) {
          dispatch(upsertOwnedShare(insertedData));
        }
        if (recipientId === currentUserId) {
          dispatch(upsertReceivedShare(insertedData));
        }
      }

      // TODO: Navigate to calendar tab
      // TODO: Update calendar tab access state

      return { success: true };
    } catch (err) {
      console.error('[useCalendarShare] Error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if calendar is already shared
   * @param {uuid} ownerId - The user who owns the calendar
   * @param {uuid} recipientId - The user who would receive the share
   */
  const checkShared = async (ownerId, recipientId) => {
    try {
      const { data, error } = await supabase
        .from('calendar_shares')
        .select('*')
        .eq('owner_id', ownerId)
        .eq('shared_with_id', recipientId)
        .limit(1);

      if (error) {
        throw error;
      }

      return data && data.length > 0;
    } catch (err) {
      console.error('[useCalendarShare] Error checking share status:', err);
      return false;
    }
  };

  return {
    acceptShare,
    checkShared,
    loading,
    error,
  };
}
